/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import QuerySubject from './QuerySubject';
import Filter from './Filter';
import Calculation from './Calculation';
import FolderItemType from './FolderItemType';
import DefLinkType from './DefLinkType';
import InstanceType from './InstanceType';
import BaseObjectType from './BaseObjectType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubject (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/Filter (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/Calculation (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/FolderItemType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/DefLinkType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/BaseObjectType (static) // new

/**
 * A helper class to resolve references in a module. Currently, it resolves the references in those areas: 1. MDTV: the MDTV is fully populated with its referenced objects for both datamodules and FM packages 2. Relationships: resolves the left, right, and link references created from both datamodule and FM packages. 3. Segment: resolves the referenced QueryItem 3. FacetDefinition: resolves the references for Context and SortItem
 */
export default class ReferenceResolver extends BaseMoserObject{

	static ID_SEPARATOR = ".";

	static EMBEDDED_MODULE_ALIAS = "ES";

	static MODULE_ALIAS_PREFIX = "M";

	static RESOLVED_BASE_MODULE_ALIAS_PREFIX = "__Base";

	static IMPORT_ALL = "*";

	static PROP_NEW = "NEW";

	static PROP_LABEL = "label";

	static PROP_COMMENT = "comment";

	static PROP_DESCRIPTION = "description";

	static PROP_LOCALREF = "localRef";

	static PROP_PROPERTY_DOT = "property.";

	static PROP_SCREENTIP = "screenTip";

	static PROP_ISHIDDEN = "hidden";

	static PROP_FORMAT = "format";

	static PROP_TAXONOMY = "taxonomy";

	static PROP_USAGE = "usage";

	static PROP_REGULARAGGREGATE = "regularAggregate";

	static PROP_FACETDEFINITION = "facetDefinition";

	static PROP_IMPORTED = "Imported";



	static resolve(module) {
		if (!module) {
			return;
		}
		var overrideMode = module.isOverrideMode();
		module.setOverrideMode(false);
		ReferenceResolver._resolveItemHierarchyLocalRef(module);
		ReferenceResolver.resolveMDTVReferences(module);
		ReferenceResolver._resolveSegmentReferences(module);
		ReferenceResolver._resolveRelationships(module);
		ReferenceResolver._resolveFacetDef(module);
		module.setOverrideMode(overrideMode);
	}

	/**
	 */
	static _resolveFacetDef(module) {
		if (!module) {
			return;
		}
		var querySubjects = module.basicGetQuerySubject();
		if (querySubjects) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var querySubject = __exp_enFor0[__i_enFor0];
				ReferenceResolver._resolveFacetDefFromQuerySubject(querySubject);
			}
		}
	}

	static _resolveFacetDefFromQuerySubject(qs) {
		if (!qs) {
			return;
		}
		var queryItemTypeList = qs.basicGetItem();
		if (queryItemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				ReferenceResolver._resolveFacetDefFromItemType(queryItemType, qs);
			}
		}
	}

	static _resolveFacetDefFromQueryItemDirect(item, qs) {
		if (!item) {
			return;
		}
		var ft = item.getFacetDefinition();
		if (!ft) {
			return;
		}
		var sl = ft.getSortList();
		if (sl) {
			var sortItems = sl.basicGetSortItem();
			if (sortItems) {
				for (var __i_enFor0 = 0, __exp_enFor0 = sortItems, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var sortItem = __exp_enFor0[__i_enFor0];
					if (!sortItem.getReferencedObject()) {
						var ref = sortItem.getRef();
						var qi = ReferenceResolver.getQueryItemByIdAndQuerySubject(ref, qs, false);
						if (qi) {
							sortItem.setReferencedObject(qi);
						}
					}
				}
			}
		}
		var context = ft.getContext();
		if (context) {
			if (!context.getReferencedObject()) {
				var ref = context.getRef();
				var qi = ReferenceResolver.getQueryItemByIdAndQuerySubject(ref, qs, false);
				context.setReferencedObject(qi);
			}
		}
	}

	static _resolveFacetDefFromItemType(queryItemType, qs) {
		var queryItem = queryItemType.getQueryItem();
		if (queryItem) {
			ReferenceResolver._resolveFacetDefFromItem(queryItem, qs);
		}
		var nestedFolder = queryItemType.getFolder();
		if (nestedFolder) {
			ReferenceResolver._resolveFacetDefFromFolder(nestedFolder, qs);
		}
	}

	static _resolveFacetDefFromItem(item, qs) {
		ReferenceResolver._resolveFacetDefFromQueryItemDirect(item, qs);
		var queryItems = item.basicGetItem();
		if (queryItems) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItems, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				ReferenceResolver._resolveFacetDefFromItemType(queryItemType, qs);
			}
		}
	}

	static _resolveFacetDefFromFolder(folder, qs) {
		var queryItemTypeList = folder.basicGetItem();
		if (queryItemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				ReferenceResolver._resolveFacetDefFromItemType(queryItemType, qs);
			}
		}
	}

	/**
	 */
	static _resolveRelationships(module) {
		if (!module) {
			return;
		}
		var relationships = module.basicGetRelationship();
		if (relationships) {
			for (var __i_enFor0 = 0, __exp_enFor0 = relationships, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var relationship = __exp_enFor0[__i_enFor0];
				ReferenceResolver.resolveRelationship(relationship, module);
			}
		}
	}

	static resolveRelationship(relationship, module) {
		ReferenceResolver.resolveCardinalityType(relationship.getLeft(), module);
		ReferenceResolver.resolveCardinalityType(relationship.getRight(), module);
		ReferenceResolver.resolveRelationshipLink(relationship);
	}

	static resolveRelationshipLink(relationship) {
		if (!relationship) {
			return;
		}
		if (relationship.getLeft() && relationship.getRight()) {
			ReferenceResolver._resolveRelationshipLinkByQuerySubjects(relationship.getLeft().getReferencedObject(), relationship.getRight().getReferencedObject(), relationship);
		}
	}

	static _resolveRelationshipLinkByQuerySubjects(resolvedObjLeft, resolvedObjRight, relationship) {
		var links = relationship.basicGetLink();
		if (links) {
			for (var __i_enFor0 = 0, __exp_enFor0 = links, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var link = __exp_enFor0[__i_enFor0];
				link.setReferencedObjectLeft(ReferenceResolver.getQueryItemByIdAndQuerySubject(link.getLeftRef(), resolvedObjLeft, false));
				link.setReferencedObjectRight(ReferenceResolver.getQueryItemByIdAndQuerySubject(link.getRightRef(), resolvedObjRight, false));
			}
		}
	}

	static resolveCardinalityType(cardinality, module) {
		if (!cardinality || !module) {
			return;
		}
		var ref = cardinality.getRef();
		var resolveObj = ReferenceResolver._getQuerySubjectById(ref, module);
		if (!resolveObj) {
			var defLinkType = ReferenceResolver._getDefLinkByIdForExpression(ref, module, false);
			if (defLinkType) {
				resolveObj = defLinkType.getReferencedObject();
			}
		}
		cardinality.setReferencedObject(resolveObj);
	}

	static _getDefLinkByIdForExpression(id, module, isIdForQueryItem) {
		if (!module) {
			return null;
		}
		var result;
		var mdtv = module.basicGetMetadataTreeView();
		if (mdtv) {
			for (var __i_enFor0 = 0, __exp_enFor0 = mdtv, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderType = __exp_enFor0[__i_enFor0];
				result = ReferenceResolver._getFolderTypeReferences(id, folderType, module, isIdForQueryItem);
			}
		}
		return result;
	}

	static _getFolderTypeReferences(id, folderType, module, isIdForQueryItem) {
		if (!module) {
			return null;
		}
		var result;
		var folderItemTypes = folderType.basicGetFolderItem();
		if (folderItemTypes) {
			for (var __i_enFor0 = 0, __exp_enFor0 = folderItemTypes, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderItemType = __exp_enFor0[__i_enFor0];
				var nestedFolder = folderItemType.getFolder();
				if (nestedFolder) {
					result = ReferenceResolver._getFolderTypeReferences(id, nestedFolder, module, isIdForQueryItem);
					if (result) {
						break;
					}
				}
				var defLinkType = folderItemType.getDefLink();
				if (defLinkType) {
					var defLinkId = defLinkType.basicGetIdForExpression();
					if (defLinkId != null) {
						if (defLinkId == id || isIdForQueryItem && id.indexOf(defLinkId) == 0) {
							result = defLinkType;
							break;
						}
					}
				}
			}
		}
		return result;
	}

	/**
	 */
	static _resolveSegmentReferences(module) {
		if (!module) {
			return;
		}
		var drillGroups = module.basicGetDrillGroup();
		if (drillGroups) {
			for (var __i_enFor0 = 0, __exp_enFor0 = drillGroups, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var drillGroup = __exp_enFor0[__i_enFor0];
				var segments = drillGroup.basicGetSegment();
				if (segments) {
					for (var __i_enFor1 = 0, __exp_enFor1 = segments, __len_enFor1 = __exp_enFor1.length;
							__i_enFor1 < __len_enFor1; ++__i_enFor1) {
						var segment = __exp_enFor1[__i_enFor1];
						ReferenceResolver.resolveSegment(segment, module);
					}
				}
			}
		}
	}

	static resolveSegment(segment, module) {
		if (segment && module) {
			var ref = segment.getRef();
			var id = segment.getIdentifier();
			if (id.indexOf("[") == 0 && id.indexOf("]") == id.length - 1 && ref.indexOf("ES") == 0) {
				var idx = ref.indexOf(".");
				if (idx > -1) {
					ref = ref.substring(idx + 1);
					segment.setRef(ref);
				}
			}
			var queryItem = ReferenceResolver._getQueryItemById(ref, module);
			if (!queryItem) {
				var defLinkType = ReferenceResolver._getDefLinkByIdForExpression(ref, module, true);
				if (defLinkType) {
					var obj = defLinkType.getReferencedObject();
					if (obj instanceof QuerySubject) {
						queryItem = ReferenceResolver.getQueryItemByIdAndQuerySubject(ref, obj, true);
					}
				}
			}
			segment.setReferencedObject(queryItem);
		}
	}

	/**
	 * Flattened QuerySubject List by MDTV reference resolving This API is requested by Smart module to be able to get the IdForExpression for querysubjects for data modules and FM packages.
	 * @param {Module} module
	 * @return {Array}  A list of querysubjects containing the duplicates if it is FM package
	 */
	static getFlattenedQuerySubjectList(module) {
		var qsFlatList = [];
		if (!module) {
			return qsFlatList;
		}
		var otFlatList = ReferenceResolver._getFlattenedBaseObjectTypeList(module);
		if (otFlatList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = otFlatList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				if (obj instanceof QuerySubject) {
					qsFlatList.push(obj);
				}
			}
		}
		return qsFlatList;
	}

	/**
	 * Flattened Filter List by MDTV reference resolving This API is requested by Smart module to be able to get the IdForExpression for filters for data modules and FM packages.
	 * @param {Module} module
	 * @return {Array}  A list of Filters
	 */
	static getFlattenedFilterList(module) {
		var filterFlatList = [];
		if (!module) {
			return filterFlatList;
		}
		var otFlatList = ReferenceResolver._getFlattenedBaseObjectTypeList(module);
		if (otFlatList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = otFlatList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				if (obj instanceof Filter) {
					filterFlatList.push(obj);
				}
			}
		}
		return filterFlatList;
	}

	/**
	 * Flattened Calculation List by MDTV reference resolving This API is requested by Smart module to be able to get the IdForExpression for Calculation for data modules and FM packages.
	 * @param {Module} module
	 * @return {Array}  A list of Calculation
	 */
	static getFlattenedCalculationList(module) {
		var calcFlatList = [];
		if (!module) {
			return calcFlatList;
		}
		var otFlatList = ReferenceResolver._getFlattenedBaseObjectTypeList(module);
		if (otFlatList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = otFlatList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				if (obj instanceof Calculation) {
					calcFlatList.push(obj);
				}
			}
		}
		return calcFlatList;
	}

	static _getFlattenedBaseObjectTypeList(module) {
		if (!module) {
			return null;
		}
		var otFlatList = module.getFlattenedBaseObjectTypeList();
		if (!otFlatList) {
			otFlatList = [];
			ReferenceResolver.resolveMDTVReferences(module, otFlatList);
		}
		return otFlatList;
	}

	/**
	 */
	static resolveMDTVReferences$0(module) {
		if (!module) {
			return;
		}
		var otFlatList = [];
		ReferenceResolver.resolveMDTVReferences$1(module, otFlatList);
	}

	static resolveMDTVReferences$1(module, otFlatList) {
		if (!module) {
			return;
		}
		var mdtv = module.basicGetMetadataTreeView();
		if (mdtv) {
			for (var __i_enFor0 = 0, __exp_enFor0 = mdtv, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderType = __exp_enFor0[__i_enFor0];
				ReferenceResolver._resolveFolderTypeReferences(null, folderType, module, otFlatList);
			}
		}
		if (otFlatList && !(otFlatList.length==0)) {
			module.setFlattenedBaseObjectTypeList(otFlatList);
		}
	}

	static _resolveFolderTypeReferences(ancestorFolderTypeId, folderType, module, otFlatList) {
		if (!module) {
			return;
		}
		var folderTypeId = folderType.getIdentifier();
		if (folderTypeId == null) {
			folderTypeId = ancestorFolderTypeId;
		}
		var folderItemTypes = folderType.basicGetFolderItem();
		if (folderItemTypes) {
			for (var __i_enFor0 = 0, __exp_enFor0 = folderItemTypes, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderItemType = __exp_enFor0[__i_enFor0];
				var ref = folderItemType.getRef();
				if (ref != null && !folderItemType.getReferencedObject()) {
					ReferenceResolver._resolveByRef(null, ref, folderItemType, module, otFlatList);
				}
				var nestedFolder = folderItemType.getFolder();
				if (nestedFolder) {
					ReferenceResolver._resolveFolderTypeReferences(folderTypeId, nestedFolder, module, otFlatList);
				}
				var defLinkType = folderItemType.getDefLink();
				if (defLinkType) {
					var idToPrepend = null;
					if (defLinkType.getIdentifier() != null) {
						idToPrepend = defLinkType.getIdentifier();
						if (folderTypeId != null) {
							idToPrepend = folderTypeId + "." + idToPrepend;
						}
					}
					ReferenceResolver._resolveByRef(idToPrepend, defLinkType.getRef(), defLinkType, module, otFlatList);
				}
			}
		}
	}

	static _resolveByRef(idToPrepend, ref, scope, module, otFlatList) {
		if (!module) {
			return;
		}
		var qs = ReferenceResolver._getQuerySubjectById(ref, module);
		if (qs) {
			ReferenceResolver._setResolvedObject(idToPrepend, scope, qs, otFlatList);
			return;
		}
		var calc = ReferenceResolver._getCalculationById(ref, module);
		if (calc) {
			ReferenceResolver._setResolvedObject(idToPrepend, scope, calc, otFlatList);
			return;
		}
		var filter = ReferenceResolver._getFilterById(ref, module);
		if (filter) {
			ReferenceResolver._setResolvedObject(idToPrepend, scope, filter, otFlatList);
			return;
		}
		var namedSet = ReferenceResolver._getNamedSetById(ref, module);
		if (namedSet) {
			ReferenceResolver._setResolvedObject(idToPrepend, scope, namedSet, otFlatList);
			return;
		}
	}

	/**
	 * update query subject label.
	 * @param {QuerySubject} qs  query subject.
	 * @param {String} label  new label.
	 */
	static _updateQSLabel(qs, label) {
		if (label != null && !(label.length === 0) && !(label == qs.getLabel())) {
			qs.setLabel(label);
		}
	}

	static _setResolvedObject(composedIdForExprn, scope, resolvedObj, otFlatList) {
		var isPackage = false;
		if (scope instanceof FolderItemType) {
			(scope).setReferencedObject(resolvedObj);
		} else if (scope instanceof DefLinkType) {
			isPackage = true;
			var defLink = scope;
			resolvedObj.setIdForExpression(composedIdForExprn);
			defLink.setReferencedObject(resolvedObj);
			defLink.setIdForExpression(composedIdForExprn);
			if (otFlatList) {
				otFlatList.push(resolvedObj);
			}
			if (resolvedObj instanceof QuerySubject) {
				var qs = resolvedObj;
				ReferenceResolver._resolveShortenedRootMember(qs, defLink.getIdForExpression());
				ReferenceResolver._updateQSLabel(qs, defLink.getLabel());
			}
		}
		if (resolvedObj instanceof QuerySubject) {
			if ((resolvedObj).getInstanceType() != InstanceType.MISSING) {
				var idForExprn = composedIdForExprn;
				if (idForExprn == null || idForExprn.length == 0) {
					idForExprn = (resolvedObj).getIdentifier();
				}
				ReferenceResolver._setQuerySubjectIdForExprn(idForExprn, resolvedObj, isPackage);
			}
		}
	}

	static _setQuerySubjectIdForExprn(idToPrepend, querySubject, isPackage) {
		var itemTypeList = querySubject.basicGetItem();
		if (itemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var itemType = __exp_enFor0[__i_enFor0];
				var queryItem = itemType.getQueryItem();
				if (queryItem && queryItem.basicGetIdForExpression() == null) {
					ReferenceResolver._setQueryItemIdForExprn(queryItem, idToPrepend);
				}
				var itemHierarchy = itemType.getItemHierarchy();
				if (itemHierarchy && itemHierarchy.basicGetIdForExpression() == null) {
					itemHierarchy.setIdForExpression(idToPrepend + "." + itemHierarchy.getIdentifier());
				}
				var folder = itemType.getFolder();
				if (folder) {
					ReferenceResolver._setFolderIdForExprn(folder, idToPrepend);
				}
			}
		}
	}

	static _resolveItemHierarchyLocalRef(module) {
		if (!module) {
			return;
		}
		var querySubjectList = module.basicGetQuerySubject();
		if (querySubjectList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjectList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var querySubject = __exp_enFor0[__i_enFor0];
				var queryItemTypeList = querySubject.basicGetItem();
				if (queryItemTypeList) {
					for (var __i_enFor1 = 0, __exp_enFor1 = queryItemTypeList, __len_enFor1 = __exp_enFor1.length;
							__i_enFor1 < __len_enFor1; ++__i_enFor1) {
						var queryItemType = __exp_enFor1[__i_enFor1];
						var hierarchy = queryItemType.getItemHierarchy();
						if (hierarchy) {
							ReferenceResolver._resolveLocalRef(hierarchy, querySubject);
						}
					}
				}
			}
		}
	}

	static _resolveLocalRef(hierarchy, querySubject) {
		if (hierarchy && querySubject) {
			var localRefList = hierarchy.basicGetLocalRef();
			if (localRefList) {
				for (var __i_enFor0 = 0, __exp_enFor0 = localRefList, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var localRef = __exp_enFor0[__i_enFor0];
					var ref = localRef.getRef();
					if (ref != null) {
						var qi = ReferenceResolver.getQueryItemByIdAndQuerySubject(ref, querySubject, false);
						localRef.setReferencedObject(qi);
					}
				}
			}
		}
	}

	static _setFolderIdForExprn(folder, idToPrepend) {
		var queryItemTypeList = folder.basicGetItem();
		if (queryItemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				var queryItem = queryItemType.getQueryItem();
				if (queryItem) {
					ReferenceResolver._setQueryItemIdForExprn(queryItem, idToPrepend);
				}
				var nestedFolder = queryItemType.getFolder();
				if (nestedFolder) {
					ReferenceResolver._setFolderIdForExprn(nestedFolder, idToPrepend);
				}
			}
		}
	}

	static _setQueryItemIdForExprn(item, idToPrepend) {
		var queryItems = item.basicGetItem();
		var newIdToPrepend = idToPrepend;
		if (newIdToPrepend != null) {
			newIdToPrepend = newIdToPrepend + "." + item.getIdentifier();
		}
		if (queryItems) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItems, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				var queryItem = queryItemType.getQueryItem();
				if (queryItem) {
					ReferenceResolver._setQueryItemIdForExprn(queryItem, newIdToPrepend);
				}
				var namedSet = queryItemType.getNamedSet();
				if (namedSet && namedSet.basicGetIdForExpression() == null) {
					namedSet.setIdForExpression(newIdToPrepend + "." + namedSet.getIdentifier());
				}
				var nestedFolder = queryItemType.getFolder();
				if (nestedFolder) {
					ReferenceResolver._setFolderIdForExprn(nestedFolder, newIdToPrepend);
				}
			}
		}
		item.setIdForExpression(newIdToPrepend);
	}

	/**
	 */
	static _getQuerySubjectById(id, module) {
		if (!module) {
			return null;
		}
		var querySubject;
		var qsList = module.basicGetQuerySubject();
		if (qsList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				if (qs.getIdentifier() == id) {
					querySubject = qs;
					break;
				}
			}
		}
		return querySubject;
	}

	static _getCalculationById(id, module) {
		if (!module) {
			return null;
		}
		var calculation;
		var calculations = module.basicGetCalculation();
		if (calculations) {
			for (var __i_enFor0 = 0, __exp_enFor0 = calculations, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var calc = __exp_enFor0[__i_enFor0];
				if (calc.getIdentifier() == id) {
					calculation = calc;
					break;
				}
			}
		}
		return calculation;
	}

	static _getFilterById(id, module) {
		if (!module) {
			return null;
		}
		var filter;
		var filterList = module.basicGetFilter();
		if (filterList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = filterList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var fltr = __exp_enFor0[__i_enFor0];
				if (fltr.getIdentifier() == id) {
					filter = fltr;
					break;
				}
			}
		}
		return filter;
	}

	static _getNamedSetById(id, module) {
		if (!module) {
			return null;
		}
		var namedSet;
		var namedSetList = module.basicGetNamedSet();
		if (namedSetList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = namedSetList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var nSet = __exp_enFor0[__i_enFor0];
				if (nSet.getIdentifier() == id) {
					namedSet = nSet;
					break;
				}
			}
		}
		return namedSet;
	}

	/**
	 * Find an object in the module using the MDTV folderItem ref
	 * @param {String} ref
	 * @param {Module} module
	 * @return {ObjectType}  the objectType that was referred to by the ref or null if not found
	 */
	static findRefById(ref, module) {
		if (!module) {
			return null;
		}
		var obj;
		if ((obj = ReferenceResolver._getQuerySubjectById(ref, module))) {
			return obj;
		}
		if ((obj = ReferenceResolver._getCalculationById(ref, module))) {
			return obj;
		}
		if ((obj = ReferenceResolver._getFilterById(ref, module))) {
			return obj;
		}
		if ((obj = ReferenceResolver._getNamedSetById(ref, module))) {
			return obj;
		}
		return null;
	}

	static _getQueryItemById(qualifiedID, module) {
		if (!module) {
			return null;
		}
		var queryItem;
		var qsId = ReferenceResolver.getFirstPart(qualifiedID);
		if (qsId != null && qsId.length > 0) {
			var qs = ReferenceResolver._getQuerySubjectById(qsId, module);
			var qiId = ReferenceResolver.getSecondPart(qualifiedID);
			if (qs && qiId != null && qiId.length > 0) {
				queryItem = ReferenceResolver.getQueryItemByIdAndQuerySubject(qiId, qs, false);
			}
		}
		return queryItem;
	}

	static getQueryItemListByIdExprListAndQuerySubject(idExprList, qs, isIdForExprn) {
		var qiList = [];
		if (!qs || !idExprList || idExprList.length==0) {
			return qiList;
		}
		var idExprMap = {};
		var items = qs.basicGetItem();
		if (!items || items.length==0) {
			return qiList;
		}
		ReferenceResolver._collectObjectByIdInItemList(items, idExprMap, isIdForExprn);
		for (var __i_enFor0 = 0, __exp_enFor0 = idExprList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var id = __exp_enFor0[__i_enFor0];
			var aQI = idExprMap[id];
			if (aQI) {
				qiList.push(aQI);
			}
		}
		return qiList;
	}

	static _collectObjectByIdInItemList(items, idExprMap, isIdForExprn) {
		if (!items || items.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var itm = __exp_enFor0[__i_enFor0];
			ReferenceResolver._collectObjectByIdInItem(itm, idExprMap, isIdForExprn);
		}
	}

	static _collectObjectByIdInItem(itm, idExprMap, isIdForExprn) {
		var qi = itm.getQueryItem();
		if (qi) {
			ReferenceResolver._updateObjectMap(qi, idExprMap, isIdForExprn);
			var childItems = qi.basicGetItem();
			ReferenceResolver._collectObjectByIdInItemList(childItems, idExprMap, isIdForExprn);
			return;
		}
		var nmSet = itm.getNamedSet();
		if (nmSet) {
			ReferenceResolver._updateObjectMap(nmSet, idExprMap, isIdForExprn);
			var childItems = nmSet.basicGetItem();
			ReferenceResolver._collectObjectByIdInItemList(childItems, idExprMap, isIdForExprn);
			return;
		}
		var fld = itm.getFolder();
		if (fld) {
			var childItems = fld.basicGetItem();
			ReferenceResolver._collectObjectByIdInItemList(childItems, idExprMap, isIdForExprn);
			return;
		}
	}

	static _updateObjectMap(baseItem, idExprMap, isIdForExprn) {
		var idExpr;
		if (isIdForExprn) {
			idExpr = baseItem.basicGetIdForExpression();
		} else {
			idExpr = baseItem.getIdentifier();
		}
		if (idExpr != null) {
			idExprMap[idExpr] = baseItem;
		}
	}

	/**
	 * Retrieve <code>QueryItem</code> by identifier within a <code>QuerySubject</code>
	 * @param {String} id  the identifier of the <code>QueryItem</code>
	 * @param {QuerySubject} qs  the <code>QuerySubject</code> of this <code>QueryItem</code>
	 * @param {boolean} isIdForExprn  true if the id is idForExpression, otherwise, it is identifier
	 * @return {BaseQueryItemType}  a reference of the <code>QueryItem</code>
	 */
	static getQueryItemByIdAndQuerySubject(id, qs, isIdForExprn) {
		if (!qs) {
			return null;
		}
		var queryItemTypeList = qs.basicGetItem();
		return ReferenceResolver._getQueryItemFromItemTypeList(id, queryItemTypeList, isIdForExprn);
	}

	static getItemHierarchyByIdAndQuerySubject(id, qs, isIdForExprn) {
		if (!qs) {
			return null;
		}
		var itemHierarchy;
		var itemTypeList = qs.basicGetItem();
		if (itemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var itemType = __exp_enFor0[__i_enFor0];
				itemHierarchy = ReferenceResolver._getItemHierarchyFromItemType(id, itemType, isIdForExprn);
				if (itemHierarchy) {
					break;
				}
			}
		}
		return itemHierarchy;
	}

	/**
	 * get Filter inside item by id.
	 * @param {String} id 	id.
	 * @param {QuerySubject} qs 	QuerySubject.
	 * @param {boolean} isIdForExprn  true: match by idForExpression.
	 * @return {Filter}  Filter or null.
	 */
	static getItemFilterByIdAndQuerySubject(id, qs, isIdForExprn) {
		if (!qs) {
			return null;
		}
		var filterItem;
		var itemTypeList = qs.basicGetItem();
		if (itemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var itemType = __exp_enFor0[__i_enFor0];
				filterItem = ReferenceResolver._getFilterFromItemType(id, itemType, isIdForExprn);
				if (filterItem) {
					break;
				}
			}
		}
		return filterItem;
	}

	static _getQueryItemFromItem(id, item, isIdForExprn) {
		if (!item || item.getIdentifier() == null) {
			return null;
		}
		if (ReferenceResolver._isIdMatch(id, item, isIdForExprn)) {
			return item;
		}
		var queryItems = item.basicGetItem();
		return ReferenceResolver._getQueryItemFromItemTypeList(id, queryItems, isIdForExprn);
	}

	static _isIdMatch(id, obj, isIdForExprn) {
		if (!obj || obj.getIdentifier() == null) {
			return false;
		}
		var itemId = isIdForExprn ? obj.basicGetIdForExpression() : obj.getIdentifier();
		if (id != null && id == itemId) {
			return true;
		}
		return false;
	}

	static _getItemHierarchyFromItemType(id, itemType, isIdForExprn) {
		var ih;
		var itemHierarchy = itemType.getItemHierarchy();
		if (itemHierarchy && ReferenceResolver._isIdMatch(id, itemHierarchy, isIdForExprn)) {
			return itemHierarchy;
		}
		var nestedFolder = itemType.getFolder();
		if (nestedFolder) {
			ih = ReferenceResolver._getItemHierarchyFromFolder(id, nestedFolder, isIdForExprn);
			if (ih) {
				return ih;
			}
		}
		return null;
	}

	static _getFilterFromItemType(id, itemType, isIdForExprn) {
		var itemFilter = itemType.getFilter();
		if (itemFilter && ReferenceResolver._isIdMatch(id, itemFilter, isIdForExprn)) {
			return itemFilter;
		}
		var flt;
		var nestedFolder = itemType.getFolder();
		if (nestedFolder) {
			flt = ReferenceResolver._getFilterFromFolder(id, nestedFolder, isIdForExprn);
			if (flt) {
				return flt;
			}
		}
		var queryItem = itemType.getQueryItem();
		if (queryItem) {
			var childItems = queryItem.basicGetItem();
			if (childItems) {
				for (var __i_enFor0 = 0, __exp_enFor0 = childItems, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var cItemType = __exp_enFor0[__i_enFor0];
					flt = ReferenceResolver._getFilterFromItemType(id, cItemType, isIdForExprn);
					if (flt) {
						return flt;
					}
				}
			}
		}
		return null;
	}

	static _getItemHierarchyFromFolder(id, folder, isIdForExprn) {
		var ih;
		var itemTypeList = folder.basicGetItem();
		if (itemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var itemType = __exp_enFor0[__i_enFor0];
				ih = ReferenceResolver._getItemHierarchyFromItemType(id, itemType, isIdForExprn);
				if (ih) {
					break;
				}
			}
		}
		return ih;
	}

	static _getFilterFromFolder(id, folder, isIdForExprn) {
		var flt;
		var itemTypeList = folder.basicGetItem();
		if (itemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var itemType = __exp_enFor0[__i_enFor0];
				flt = ReferenceResolver._getFilterFromItemType(id, itemType, isIdForExprn);
				if (flt) {
					break;
				}
			}
		}
		return flt;
	}

	static _getQueryItemFromFolder(id, folder, isIdForExprn) {
		var queryItemTypeList = folder.basicGetItem();
		return ReferenceResolver._getQueryItemFromItemTypeList(id, queryItemTypeList, isIdForExprn);
	}

	/**
	 * Retrieve <code>BaseQueryItemType</code> by identifier within a list of <code>ItemType</code>
	 * @param {String} id  the identifier or idForExpression of the <code>BaseQueryItemType</code> to retrieve
	 * @param {Array} queryItemTypeList  the list <code>ItemType</code> to iterate over
	 * @param {boolean} isIdForExprn  true if the id is idForExpression, otherwise, it is identifier
	 * @return {BaseQueryItemType}  a reference of the child <code>BaseQueryItemType</code>
	 */
	static _getQueryItemFromItemTypeList(id, queryItemTypeList, isIdForExprn) {
		var qi;
		if (queryItemTypeList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = queryItemTypeList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var queryItemType = __exp_enFor0[__i_enFor0];
				qi = ReferenceResolver._getQueryItemFromItemType(id, queryItemType, isIdForExprn);
				if (qi) {
					return qi;
				}
			}
		}
		return null;
	}

	static _getQueryItemFromItemType(id, queryItemType, isIdForExprn) {
		var qi;
		var queryItem = queryItemType.getQueryItem();
		if (queryItem) {
			qi = ReferenceResolver._getQueryItemFromItem(id, queryItem, isIdForExprn);
			if (qi) {
				return qi;
			}
		}
		var calc = queryItemType.getCalculation();
		if (calc) {
			qi = ReferenceResolver._getQueryItemFromItem(id, calc, isIdForExprn);
			if (qi) {
				return qi;
			}
		}
		var namedSet = queryItemType.getNamedSet();
		if (namedSet && namedSet.getIdentifier() != null && ReferenceResolver._isIdMatch(id, namedSet, isIdForExprn)) {
			return namedSet;
		}
		var nestedFolder = queryItemType.getFolder();
		if (nestedFolder) {
			qi = ReferenceResolver._getQueryItemFromFolder(id, nestedFolder, isIdForExprn);
			if (qi) {
				return qi;
			}
		}
		return null;
	}

	/**
	 * Retrieve <code>BaseQueryItemType</code> by identifier within a parent <code>Calculation</code>
	 * @param {String} id  the identifier or idForExpression of the <code>BaseQueryItemType</code> to retrieve
	 * @param {Calculation} calc  the parent <code>Calculation</code> of this child <code>BaseQueryItemType</code>
	 * @param {boolean} isIdForExprn  true if the id is idForExpression, otherwise, it is identifier
	 * @return {BaseQueryItemType}  a reference of the child <code>BaseQueryItemType</code>
	 */
	static _getQueryItemByIdAndCalculation(id, calc, isIdForExprn) {
		if (!calc) {
			return null;
		}
		var queryItemTypeList = calc.basicGetItem();
		return ReferenceResolver._getQueryItemFromItemTypeList(id, queryItemTypeList, isIdForExprn);
	}

	static getFirstPart(qualifiedID) {
		if (qualifiedID == null) {
			return null;
		}
		var pos = qualifiedID.indexOf(".");
		if (pos < 0) {
			return "";
		}
		return qualifiedID.substring(0, pos);
	}

	static getSecondPart(qualifiedID) {
		if (qualifiedID == null) {
			return null;
		}
		var pos = qualifiedID.indexOf(".");
		if (pos < 0) {
			return "";
		}
		return qualifiedID.substring(pos + 1);
	}

	static _resolveShortenedRootMember(querySubject, prefix) {
		if (!querySubject) {
			return;
		}
		var itemList = querySubject.basicGetItem();
		if (!itemList || itemList.length==0) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = itemList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var qi = item.getQueryItem();
			if (qi) {
				var rootMember = qi.getRootMember();
				if (rootMember != null && !(rootMember.indexOf(prefix) == 0 || rootMember.indexOf(ReferenceResolver._squareBracket(prefix)) == 0) && !(rootMember.length === 0) && ReferenceResolver._isShortened(rootMember)) {
					qi.setRootMember(prefix + "." + rootMember);
				}
			}
		}
	}

	/**
	 * Put square brackets around given string.
	 * @param  prefix
	 * @return {String}
	 */
	static _squareBracket(theString) {
		return "[" + theString + "]";
	}

	static _isShortened(rootMember) {
		var mdParts = rootMember.split(new RegExp("\\."));
		if (mdParts && mdParts.length == 2) {
			return mdParts[1].indexOf("]->[") > 0 ? true : false;
		}
		return false;
	}

	/**
	 * Retrieve <code>MoserObject</code> by idForExpression
	 * @param {String} idForExprn  the idForExpression to look for.
	 * @param {Module} module 	the data module
	 * @return {MoserObject}  a reference of the <code>MoserObject</code> including QuerySubject, QueryItem, Calculation and Filter
	 */
	static getMoserObjectByIdForExpression(idForExprn, module) {
		var mdtv = module.basicGetMetadataTreeView();
		var moserObject;
		if (mdtv) {
			for (var __i_enFor0 = 0, __exp_enFor0 = mdtv, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderType = __exp_enFor0[__i_enFor0];
				moserObject = ReferenceResolver._getMoserObjectFromFolderType(idForExprn, folderType);
				if (moserObject) {
					break;
				}
			}
		}
		return moserObject;
	}

	static _getMoserObjectFromFolderType(idForExprn, folderType) {
		var folderItemTypes = folderType.basicGetFolderItem();
		var moserObject;
		if (folderItemTypes) {
			for (var __i_enFor0 = 0, __exp_enFor0 = folderItemTypes, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var folderItemType = __exp_enFor0[__i_enFor0];
				moserObject = ReferenceResolver._getMoserObjectByIdForExprnAndRefObj(idForExprn, folderItemType.getReferencedObject());
				if (moserObject) {
					break;
				}
				var nestedFolder = folderItemType.getFolder();
				if (nestedFolder) {
					moserObject = ReferenceResolver._getMoserObjectFromFolderType(idForExprn, nestedFolder);
					if (moserObject) {
						break;
					}
				}
				var defLinkType = folderItemType.getDefLink();
				if (defLinkType) {
					moserObject = ReferenceResolver._getMoserObjectByIdForExprnAndRefObj(idForExprn, defLinkType.getReferencedObject());
					if (moserObject) {
						break;
					}
				}
			}
		}
		return moserObject;
	}

	static _getMoserObjectByIdForExprnAndRefObj(idForExprn, refObj) {
		if (refObj) {
			if (ReferenceResolver._foundMoserObjectByIdForExprn(idForExprn, refObj)) {
				return refObj;
			} else if (refObj instanceof QuerySubject) {
				var obj = ReferenceResolver.getQueryItemByIdAndQuerySubject(idForExprn, refObj, true);
				if (!obj) {
					obj = ReferenceResolver.getItemHierarchyByIdAndQuerySubject(idForExprn, refObj, true);
				}
				if (!obj) {
					obj = ReferenceResolver.getItemFilterByIdAndQuerySubject(idForExprn, refObj, true);
				}
				return obj;
			} else if (refObj instanceof Calculation) {
				return ReferenceResolver._getQueryItemByIdAndCalculation(idForExprn, refObj, true);
			}
		}
		return null;
	}

	static _foundMoserObjectByIdForExprn(idForExprn, refObj) {
		if (refObj && refObj instanceof BaseObjectType) {
			var baseObjType = refObj;
			var id = baseObjType.getIdForExpression();
			if (id != null && id == idForExprn) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Bind the given id to a moser metadata object.
	 * @param {Module} module  - Module that is searched for the object.
	 * @param {String} multiPartID  - input id.
	 * @return {MoserObject}  MoserObject.
	 */
	static bindMoserObject(module, multiPartID) {
		var mo = ReferenceResolver.getMoserObjectByIdForExpression(multiPartID, module);
		if (!mo) {
			mo = ReferenceResolver._getQueryItemById(multiPartID, module);
		}
		if (!mo) {
			mo = ReferenceResolver._getCalculationById(multiPartID, module);
		}
		if (!mo) {
			mo = ReferenceResolver.findRefById(multiPartID, module);
		}
		if (!mo) {
			for (var __i_enFor0 = 0, __exp_enFor0 = module.getUseSpec(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var useSpec = __exp_enFor0[__i_enFor0];
				var alias = useSpec.getIdentifier();
				if (alias != null) {
					var newID = alias + "." + multiPartID;
					mo = ReferenceResolver.getMoserObjectByIdForExpression(newID, module);
					if (mo) {
						break;
					}
				}
			}
			if (!mo) {
				var alias = ReferenceResolver.strBetween(multiPartID, "[", "].");
				if (alias != null) {
					for (var __i_enFor1 = 0, __exp_enFor1 = module.getUseSpec(), __len_enFor1 = __exp_enFor1.length;
							__i_enFor1 < __len_enFor1; ++__i_enFor1) {
						var useSpec = __exp_enFor1[__i_enFor1];
						if (alias == useSpec.getIdentifier()) {
							var newID = alias + "." + ReferenceResolver.strAfter(multiPartID, "].");
							return ReferenceResolver.bindMoserObject(module, newID);
						}
					}
				}
			}
		}
		return mo;
	}

	/**
	 * @param {String} inString
	 * @param  string
	 * @return {String}
	 */
	static strAfter(inString, after) {
		if (inString == null || inString.length === 0) {
			return inString;
		}
		if (after == null) {
			return "";
		}
		var pos = inString.indexOf(after);
		if (pos == -1) {
			return "";
		}
		return inString.substring(pos + after.length);
	}

	/**
	 * @param {String} inString
	 * @param {String} str1
	 * @param {String} str2
	 * @return {String}
	 */
	static strBetween(inString, str1, str2) {
		if (inString == null || str1 == null || str2 == null) {
			return null;
		}
		var idxStr1 = inString.indexOf(str1);
		if (idxStr1 == -1) {
			return null;
		}
		var idxStr2 = inString.indexOf(str2, idxStr1 + str1.length);
		if (idxStr2 == -1) {
			return null;
		}
		return inString.substring(idxStr1 + str1.length, idxStr2);
	}

	static resolveMDTVReferences(a0, a1) {
		var args = arguments;
		if (args.length == 1) {
			return ReferenceResolver.resolveMDTVReferences$0(a0);
		}
		return ReferenceResolver.resolveMDTVReferences$1(a0, a1);
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ReferenceResolver.js